import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import { purchaseAPI } from '../../api';

const UserCart = () => {
  const { cart, removeFromCart, updateQuantity, getCartTotal, clearCart } = useCart();
  const { user, refreshUser } = useAuth();
  const [discountInfo, setDiscountInfo] = useState(null);
  const cartTotal = getCartTotal();
  const walletBalance = user?.wallet?.depositWallet || 0;
  const repurchaseWalletBalance = user?.wallet?.repurchaseWallet || 0;
  const canPayWithWallet = walletBalance >= (discountInfo?.finalAmount || cartTotal);
  const canPayWithRepurchaseWallet = repurchaseWalletBalance >= (discountInfo?.finalAmount || cartTotal);
  const canProceedToCheckout = canPayWithWallet || canPayWithRepurchaseWallet;

  useEffect(() => {
    if (refreshUser) {
      refreshUser();
    }
  }, [refreshUser]);

  // Calculate discount on component mount and when cart changes
  useEffect(() => {
    const calculateDiscount = async () => {
      try {
        // Check if user has previous purchases
        const response = await purchaseAPI.getMyPurchases({ limit: 1 });
        const existingPurchases = response?.data?.purchases || [];
        const isFirstPurchase = existingPurchases.length === 0;
        
        let discountPercentage = 0;
        let discountAmount = 0;
        let finalAmount = cartTotal;

        if (isFirstPurchase) {
          discountPercentage = 5; // 5% discount for first purchase
          discountAmount = cartTotal * 0.05;
          finalAmount = cartTotal - discountAmount;
        } else {
          discountPercentage = 10; // 10% discount for subsequent purchases
          discountAmount = cartTotal * 0.10;
          finalAmount = cartTotal - discountAmount;
        }

        setDiscountInfo({
          originalAmount: cartTotal,
          discountPercentage,
          discountAmount,
          finalAmount,
          isFirstPurchase
        });
      } catch (err) {
        console.error('Error calculating discount:', err);
        setDiscountInfo({
          originalAmount: cartTotal,
          discountPercentage: 0,
          discountAmount: 0,
          finalAmount: cartTotal,
          isFirstPurchase: false
        });
      }
    };

    if (cart.length > 0) {
      calculateDiscount();
    }
  }, [cart, cartTotal]);

  // Debug: Log cart state
  console.log('UserCart state:', { cart, cartTotal, user });

  if (cart.length === 0) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <div className="text-6xl mb-4">🛒</div>
          <h2 className="text-2xl font-bold text-gray-700 mb-4">Your cart is empty</h2>
          <p className="text-gray-600 mb-8">Start shopping to add items to your cart.</p>
          <Link to="/user/products">
            <button className="px-8 py-3 bg-[#E4572E] text-white rounded-lg hover:bg-[#c73e1a] transition-colors font-semibold">
              Continue Shopping
            </button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-[#0F2F3D] mb-2">Shopping Cart</h1>
        <p className="text-gray-500">{cart.length} item(s) in cart</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Cart Items */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Product</th>
                    <th className="px-6 py-4 text-center text-sm font-semibold text-gray-700">Price</th>
                    <th className="px-6 py-4 text-center text-sm font-semibold text-gray-700">Quantity</th>
                    <th className="px-6 py-4 text-right text-sm font-semibold text-gray-700">Total</th>
                    <th className="px-6 py-4 text-center text-sm font-semibold text-gray-700">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {cart.map((item) => (
                    <tr key={item._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-4">
                          {item.image && (
                            <img 
                              src={item.image} 
                              alt={item.name} 
                              className="w-16 h-16 object-cover rounded-lg"
                            />
                          )}
                          <div>
                            <div className="font-semibold text-[#0F2F3D] line-clamp-1">{item.name}</div>
                            <div className="text-xs text-gray-500 mt-1">{item.category?.replace(/_/g, ' ')}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <div className="text-sm font-semibold text-[#E4572E]">Rs. {item.price?.toLocaleString()}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-center gap-3">
                          <button
                            onClick={() => updateQuantity(item._id, item.quantity - 1)}
                            className="w-8 h-8 rounded-full border border-gray-300 hover:bg-gray-100 transition-colors flex items-center justify-center text-gray-600 font-semibold"
                          >
                            −
                          </button>
                          <input
                            type="number"
                            value={item.quantity}
                            onChange={(e) => {
                              const val = parseInt(e.target.value) || 0;
                              if (val > 0) updateQuantity(item._id, val);
                            }}
                            className="w-12 text-center border border-gray-300 rounded-lg py-1 text-sm"
                          />
                          <button
                            onClick={() => updateQuantity(item._id, item.quantity + 1)}
                            className="w-8 h-8 rounded-full border border-gray-300 hover:bg-gray-100 transition-colors flex items-center justify-center text-gray-600 font-semibold"
                          >
                            +
                          </button>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="text-sm font-bold text-[#0F2F3D]">
                          Rs. {(item.price * item.quantity).toLocaleString()}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <button
                          onClick={() => removeFromCart(item._id)}
                          className="text-red-600 hover:bg-red-50 px-3 py-2 rounded-lg transition-colors font-semibold text-sm"
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="mt-6 flex gap-4">
            <Link to="/user/products">
              <button className="px-6 py-3 bg-gray-200 text-[#0F2F3D] rounded-lg hover:bg-gray-300 transition-colors font-semibold">
                Continue Shopping
              </button>
            </Link>
            <button
              onClick={clearCart}
              className="px-6 py-3 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors font-semibold"
            >
              Clear Cart
            </button>
          </div>
        </div>

        {/* Cart Summary */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow-md p-6 sticky top-24">
            <h3 className="text-xl font-bold text-[#0F2F3D] mb-6">Order Summary</h3>
            
            {/* Wallet Balances */}
            <div className="mb-6 p-4 bg-green-50 rounded-lg border border-green-200">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-semibold text-green-800">Deposit Wallet:</span>
                <span className="text-lg font-bold text-green-600">Rs. {walletBalance.toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm font-semibold text-green-800">Repurchase Wallet:</span>
                <span className="text-lg font-bold text-green-600">Rs. {repurchaseWalletBalance.toLocaleString()}</span>
              </div>
              {!canPayWithWallet && !canPayWithRepurchaseWallet && cartTotal > 0 && (
                <div className="mt-2 text-xs text-red-600">
                  Need Rs. {((discountInfo?.finalAmount || cartTotal) - Math.max(walletBalance, repurchaseWalletBalance)).toLocaleString()} more to pay with wallet
                </div>
              )}
            </div>
            
            <div className="space-y-4 mb-6 pb-6 border-b border-gray-200">
              <div className="flex justify-between text-gray-700">
                <span>Subtotal:</span>
                <span className="font-semibold">Rs. {cartTotal.toLocaleString()}</span>
              </div>
              {discountInfo && discountInfo.discountPercentage > 0 && (
                <div className="flex justify-between text-green-600">
                  <span>Discount ({discountInfo.discountPercentage}% {discountInfo.isFirstPurchase ? '- First Purchase!' : '- Loyalty Discount!'}):</span>
                  <span className="font-semibold">-Rs. {discountInfo.discountAmount.toLocaleString()}</span>
                </div>
              )}
              <div className="flex justify-between text-gray-700">
                <span>Shipping:</span>
                <span className="font-semibold text-blue-600">Free</span>
              </div>
              <div className="flex justify-between text-gray-700">
                <span>Tax (0%):</span>
                <span className="font-semibold">Rs. 0</span>
              </div>
            </div>

            <div className="flex justify-between items-center mb-8">
              <span className="text-lg font-bold text-[#0F2F3D]">Total:</span>
              <div className="text-right">
                {discountInfo && discountInfo.discountPercentage > 0 && (
                  <div className="text-sm text-gray-500 line-through">
                    Rs. {discountInfo.originalAmount.toLocaleString()}
                  </div>
                )}
                <span className="text-2xl font-bold text-[#E4572E]">Rs. {(discountInfo?.finalAmount || cartTotal).toLocaleString()}</span>
              </div>
            </div>

            {(discountInfo?.finalAmount || cartTotal) < 279 && (
              <div className="text-sm text-red-600 font-semibold">
                Minimum order value is ₹279. Please add more items.
              </div>
            )}

            {discountInfo && discountInfo.discountPercentage > 0 && (
              <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                <p className="text-xs text-green-800 font-semibold">
                  🎉 {discountInfo.isFirstPurchase ? 'First Purchase Discount Applied!' : 'Loyalty Discount Applied!'}
                </p>
                <p className="text-xs text-green-800 mt-1">
                  You saved Rs. {discountInfo.discountAmount.toLocaleString()} on this order!
                </p>
              </div>
            )}

            {canProceedToCheckout ? (
              <Link to="/checkout">
                <button className="w-full px-6 py-3 bg-[#E4572E] text-white rounded-lg hover:bg-[#c73e1a] transition-colors font-bold text-lg">
                  Proceed to Checkout
                </button>
              </Link>
            ) : (
              <button 
                disabled 
                className="w-full px-6 py-3 bg-gray-400 text-gray-200 rounded-lg cursor-not-allowed font-bold text-lg"
                title="Insufficient wallet balance"
              >
                Proceed to Checkout (Insufficient Balance)
              </button>
            )}

            <div className="mt-4 space-y-2">
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-xs text-blue-800">
                  ✓ Free shipping on all orders
                </p>
                <p className="text-xs text-blue-800 mt-2">
                  ✓ Earn commission on this purchase
                </p>
              </div>
              {canPayWithWallet && (
                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <p className="text-xs text-green-800 font-semibold">
                    ✓ You can pay using your deposit wallet
                  </p>
                </div>
              )}
              {canPayWithRepurchaseWallet && (
                <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                  <p className="text-xs text-purple-800 font-semibold">
                    ✓ You can pay using your repurchase wallet
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserCart;
